import React, { useRef, useState } from 'react';
import { Button } from 'rebass';
import { FiDownload, FiVideo, FiMusic, FiMessageSquare, FiFile, FiAlignLeft } from 'react-icons/fi';
import styled from 'styled-components';
import helpers from 'utils/helpers';
import config from 'config';
import Portal from 'components/Portal';
import { PositionerV2 } from 'components/Positioner';
import OutsideClickHandler from 'react-outside-click-handler';
import { SESSION_RECORDING_VENDORS } from 'utils/constants';

const VENDOR_ICONS = {
    [SESSION_RECORDING_VENDORS.ZOOM]: { src: 'zoom2.svg', alt: 'Zoom' }
};

const RECORDING_TYPE_ICONS = {
    MP4: FiVideo,
    M4A: FiMusic,
    MP3: FiMusic,
    WAV: FiMusic,
    TRANSCRIPT: FiAlignLeft,
    VTT: FiAlignLeft,
    CHAT: FiMessageSquare,
    CC: FiAlignLeft,
    CSV: FiFile,
    TIMELINE: FiAlignLeft
};

const Recordings = styled.div`
    background: #fff;
    border-radius: 8px;
    box-shadow: 0 0 4px 0 rgba(0, 0, 0, 0.25);
    padding: 6px 0px;
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 200px;
`;

const RecordingItem = styled.a`
    display: flex;
    font-size: 14px;
    align-items: center;
    line-height: 100%;
    padding: 6px 10px;
    text-decoration: none;
    color: inherit;
    gap: 10px;

    .download-icon {
        color: #929db0;
        font-size: 14px;
        margin: 4px 4px 4px auto;
        opacity: 0;
        transition: opacity 0.15s;
    }

    &:hover {
        color: inherit;
        background-color: #fbfbfb;

        .download-icon {
            opacity: 1;
        }
    }
`;

/**
 * Dropdown button that displays session recording files in a positioned popover
 * @param {Object} props - Component props
 * @param {Array<Object>} props.recordings - Array of recording session objects
 * @param {string} props.recordings[].vendor - Recording vendor (e.g. 'zoom')
 * @param {string} props.recordings[].play_password - Password for accessing recordings
 * @param {Array<Object>} props.recordings[].recording_files - Array of recording files in the session
 * @returns {JSX.Element|null} View button with dropdown or null if no playable files
 */
function SessionRecordingsDropdown({ recordings }) {
    const anchorRef = useRef(null);
    const [isOpen, setIsOpen] = useState(false);

    if (!Array.isArray(recordings) || !recordings.length) return null;

    const vendorIcon = VENDOR_ICONS[recordings[0].vendor];

    const files = recordings.flatMap(r =>
        (r.recording_files || []).filter(f => f.play_url).map(f => ({ ...f, play_password: r.play_password }))
    );

    if (!files.length) return null;

    return (
        <>
            <Button
                ref={anchorRef}
                type="button"
                variant="secondary-gray"
                ml={1}
                style={{ flexShrink: 0 }}
                onClick={e => {
                    e.stopPropagation();
                    setIsOpen(prev => !prev);
                }}
            >
                {vendorIcon && (
                    <img
                        src={`${config.UI_SUFFIX}/${vendorIcon.src}`}
                        alt={vendorIcon.alt}
                        height="20px"
                        style={{ flexShrink: 0, marginRight: '4px' }}
                    />
                )}{' '}
                View
            </Button>
            <Portal isOpen={isOpen}>
                <PositionerV2 anchorRef={anchorRef}>
                    <OutsideClickHandler onOutsideClick={() => setIsOpen(false)}>
                        <Recordings onClick={e => e.stopPropagation()}>
                            {files.map((file, i) => (
                                <RecordingItem
                                    key={file.id || i}
                                    href={`${file.play_url}?pwd=${file.play_password}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    {React.createElement(
                                        RECORDING_TYPE_ICONS[file.file_type?.toUpperCase()] || FiFile,
                                        { style: { flexShrink: 0 }, size: 14 }
                                    )}
                                    <div>{helpers.formatRecordingType(file.file_type, file.recording_type)}</div>
                                    <small className="text-secondary">({helpers.humanFileSize(file.file_size)})</small>
                                    <FiDownload className="download-icon" />
                                </RecordingItem>
                            ))}
                        </Recordings>
                    </OutsideClickHandler>
                </PositionerV2>
            </Portal>
        </>
    );
}

export default SessionRecordingsDropdown;
